import { useState } from 'react';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { SettingsEmpty } from '~/pages/SchoolSettings/SettingsPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import ClassroomDialog from './ClassroomDialog';
import { useClassroomsQuery, useDeleteClassroom } from './useClassroomOperations';

interface Props {
  schoolId: string;
}

interface ClassroomRow {
  id: string;
  name: string;
  description?: string | null;
  meetingCount: number;
}

export default function ClassroomsEditor({ schoolId }: Props) {
  const { data, loading, refetch } = useClassroomsQuery(schoolId);
  const [deleteClassroom] = useDeleteClassroom();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClassroomRow | null>(null);
  const [deleting, setDeleting] = useState<ClassroomRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const classrooms = (data?.school?.classrooms ?? []) as ReadonlyArray<ClassroomRow>;

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteError(null);
    setRefreshError(null);
    let result;
    try {
      result = await deleteClassroom({ variables: { input: { id: deleting.id } } });
    } catch (deleteFailure) {
      const detail = deleteFailure instanceof Error && deleteFailure.message ? `: ${deleteFailure.message}` : '';
      setDeleteError(`Unable to delete this location${detail}`);
      return;
    }

    const errors = result.data?.deleteClassroom?.userErrors ?? [];
    if (errors.length > 0) {
      setDeleteError(errors.map((item) => item.message).join(', '));
      return;
    }

    setDeleting(null);
    try {
      await refetch();
    } catch (refreshFailure) {
      const detail = refreshFailure instanceof Error && refreshFailure.message ? `: ${refreshFailure.message}` : '';
      setRefreshError(`Location deleted, but locations could not be refreshed${detail}. Reload the page to see the saved changes.`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => { setRefreshError(null); setEditing(null); setDialogOpen(true); }}
        >
          <Plus className="h-4 w-4" />
          Add location
        </Button>
      </div>

      {refreshError && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {refreshError}
        </div>
      )}

      {loading && classrooms.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">Loading locations…</p>
      ) : classrooms.length === 0 ? (
        <SettingsEmpty
          action={
            <Button
              className="gap-2"
              onClick={() => { setRefreshError(null); setEditing(null); setDialogOpen(true); }}
            >
              <Plus className="h-4 w-4" />
              Add location
            </Button>
          }
        >
          No rooms or locations yet. Add classrooms, labs, fields, or anywhere else a class meets.
        </SettingsEmpty>
      ) : (
        <ul className="divide-y rounded-lg border">
          {classrooms.map((classroom) => (
            <li key={classroom.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="font-medium">{classroom.name}</p>
                  {classroom.description && (
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {classroom.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="mr-1 text-sm tabular-nums text-muted-foreground">
                  {classroom.meetingCount} meeting{classroom.meetingCount === 1 ? '' : 's'}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Edit ${classroom.name}`}
                  onClick={() => { setRefreshError(null); setEditing(classroom); setDialogOpen(true); }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${classroom.name}`}
                  disabled={classroom.meetingCount > 0}
                  title={classroom.meetingCount > 0 ? 'Remove this location from its class meetings before deleting it.' : undefined}
                  onClick={() => { setDeleteError(null); setDeleting(classroom); }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ClassroomDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        schoolId={schoolId}
        classroom={editing}
        onComplete={async () => {
          await refetch();
          setRefreshError(null);
        }}
        onRefreshError={setRefreshError}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This location can only be deleted when it is not assigned to a class meeting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <div className="text-sm text-destructive">{deleteError}</div>}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); void handleDelete(); }}>Delete location</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
